import { format, isValid, parse } from 'date-fns';
import React, { useCallback, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { STATEMENT_STATUSES } from '_constants/statements';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious } from '_hooks';
import { getFileTypes } from '_store/fileTypes/slice';
import { showModal } from '_store/modals/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectData, selectLoading } from '_store/statements/selector';
import {
  downloadStatement,
  getStatements,
  initialParams,
  refreshParams,
  uploadStatement,
} from '_store/statements/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Paginate from 'components/atomic/Paginate';
import Table from 'components/atomic/Table';
import UploadButton from 'components/atomic/UploadButton';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import StatementsList from './StatementsList';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.small}px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBody = styled.div``;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spaces.small2X}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;

  &:hover {
    border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.16)};
    background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};

    svg {
      path {
        stroke: ${theme.colors.indigo.primary};
      }
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const StyledPaginateContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
`;

const StyledDeleteIconContainer = styled.span`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  cursor: pointer;

  &:hover {
    svg {
      path {
        stroke: ${theme.colors.red.primary};
      }
    }
  }
`;

const getColumns = ({ handleDelete, handleDownload, handleUpload }) => {
  return [
    {
      dataIndex: 'document',
      title: null,
      width: '5%',
      minWidth: '30px',
      render: () => <Icon alias={ICON_ALIASES.document} color={theme.colors.gray.secondary} />,
    },
    {
      dataIndex: 'type',
      title: 'Type',
      width: '15%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.type?.replace(/([A-Z])/g, ' $1').trim()}
        </Text>
      ),
    },
    {
      dataIndex: 'description',
      title: 'Description',
      width: '26%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.description}
        </Text>
      ),
    },
    {
      dataIndex: 'year',
      title: 'Year',
      width: '6%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.year}
        </Text>
      ),
    },
    {
      dataIndex: 'startDate',
      title: 'Start Date',
      width: '10%',
      render: (row) => {
        const parsedDate = parse(row?.startDate, 'yyyy-MM-dd', new Date());

        return (
          <Text size={theme.fontSizes.small} weight={400}>
            {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : row?.startDate}
          </Text>
        );
      },
    },
    {
      dataIndex: 'endDate',
      title: 'End Date',
      width: '10%',
      render: (row) => {
        const parsedDate = parse(row?.endDate, 'yyyy-MM-dd', new Date());

        return (
          <Text size={theme.fontSizes.small} weight={400}>
            {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : row?.endDate}
          </Text>
        );
      },
    },
    {
      dataIndex: 'status',
      title: 'Status',
      width: '10%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.status?.replace(/([A-Z])/g, ' $1').trim()}
        </Text>
      ),
    },
    {
      dataIndex: 'download',
      title: null,
      width: '5%',
      minWidth: '30px',
      render: (row) => (
        <StyledIconContainer
          disabled={row?.status === STATEMENT_STATUSES.requested}
          title={row?.fileName}
          onClick={handleDownload(row?.id, row?.fileName)}
        >
          <Icon alias={ICON_ALIASES.arrowDownCircle} color={theme.colors.gray.secondary} />
        </StyledIconContainer>
      ),
    },
    {
      dataIndex: 'upload',
      title: null,
      width: '10%',
      render: (row) => (
        <StyledButtonContainer>
          <UploadButton
            disabled={row?.status === STATEMENT_STATUSES.confirmed}
            color={theme.colors.indigo.primary}
            onChange={handleUpload(row?.id)}
          >
            Upload file
          </UploadButton>
        </StyledButtonContainer>
      ),
    },
    {
      dataIndex: 'delete',
      title: null,
      width: '3%',
      render: (row) => (
        <StyledDeleteIconContainer onClick={handleDelete(row?.id)}>
          <Icon alias={ICON_ALIASES.trash} color={theme.colors.gray.secondary} height={18} width={18} />
        </StyledDeleteIconContainer>
      ),
    },
  ];
};

const StatementsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const loading = useSelector(selectLoading);
  const data = useSelector(selectData);

  const handleDelete = useCallback(
    (statementId) => () => {
      dispatch(showModal({ key: MODAL_KEYS.statementDelete, data: { productId, statementId } }));
    },
    [productId]
  );

  const handleDownload = useCallback(
    (statementId, fileName) => () => {
      dispatch(downloadStatement({ customerId, productId, statementId, fileName }));
    },
    [customerId, productId]
  );

  const handleAdd = useCallback(() => {
    dispatch(showModal({ key: MODAL_KEYS.documentUpload, data: { productId } }));
  }, [productId]);

  const handleUpload = useCallback(
    (statementId) => (file) => {
      dispatch(uploadStatement({ customerId, productId, statementId, file }));
    },
    [customerId, productId]
  );

  const handlePageChange = useCallback(
    (page) => {
      dispatch(refreshParams({ page }));
      dispatch(getStatements({ customerId, productId }));
    },
    [customerId, productId]
  );

  useEffect(() => {
    if (customerId && productId && prevProductId !== productId) {
      dispatch(refreshParams(initialParams));
      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  return (
    <Card>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600}>
            Upload Your Statements
          </Text>
        </StyledTitleContainer>
        <div>
          <Button outlined disabled={!productId} color={theme.colors.indigo.primary} onClick={handleAdd}>
            + Add statement
          </Button>
        </div>
      </StyledHeader>
      {!isMobile && (
        <StyledBody>
          <Table
            columns={getColumns({ handleDelete, handleDownload, handleUpload })}
            rows={data?.elements}
            loading={loading}
            skeletonRowsNum={data?.maxPerPage}
          />
          {!data?.totalPages && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={3}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </StyledBody>
      )}
      {isMobile && (
        <StyledBody>
          <StatementsList
            items={data?.elements}
            loading={loading}
            onUpload={handleUpload}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
          {!data?.totalPages && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={0}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </StyledBody>
      )}
    </Card>
  );
};

export default StatementsView;
